// ── useOrg ──
// React-state wrapper around theme/org-configs.js localStorage-backed active org.
// On mount: hydrates orgId from localStorage. On setOrgId: writes via setActiveOrg
// then mirrors into local state. Subscribes to the window 'storage' event so
// cross-tab changes propagate. Phase 3 will swap App.jsx's inline activeOrg
// state for this hook; for now the hook stands alone (no App.jsx delta).
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ORG_CONFIGS, getActiveOrg, setActiveOrg } from '../theme/org-configs.js';

const STORAGE_KEY = 'novo_active_org';

const readOrgIdFromStorage = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch { /* ignore */ return null; }
};

const DEFAULT_VALUE = {
  orgId: null,
  org: null,
  setOrgId: () => {},
  allOrgs: Object.values(ORG_CONFIGS),
};

export const OrgContext = createContext(DEFAULT_VALUE);

export function OrgProvider({ children }) {
  const [orgId, _setOrgId] = useState(() => readOrgIdFromStorage());

  const setOrgId = useCallback((nextId) => {
    setActiveOrg(nextId);
    _setOrgId(nextId);
  }, []);

  // Cross-tab sync: when another tab writes the localStorage key, mirror it.
  useEffect(() => {
    const onStorage = (ev) => {
      if (ev && ev.key && ev.key !== STORAGE_KEY) return;
      _setOrgId(readOrgIdFromStorage());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const org = orgId ? (ORG_CONFIGS[orgId] || getActiveOrg()) : null;
  const value = {
    orgId,
    org,
    setOrgId,
    allOrgs: Object.values(ORG_CONFIGS),
  };

  return React.createElement(OrgContext.Provider, { value }, children);
}

export function useOrg() {
  return useContext(OrgContext);
}
